import { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { Currencies } from '~/constants/currencies';
import { getFrequencyLabel } from '~/constants/depositFrequency';
import { dispatchError } from '~/containers/ErrorContainer/errorEventListener';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { PosthogHelper } from '~/helpers/PosthogHelper';
import { SentryHelper } from '~/helpers/Sentry/SentryHelper';
import { LocalStorageHelper } from '~/helpers/StorageHelper';
import { useTracking } from '~/hooks/tracking/useTracking';
import { useAuthentication } from '~/hooks/useAuthentication';
import { useConfirmDeposit } from '~/hooks/useConfirmDeposit';
import { ModalMultiStepProps } from '~/models/deposits/ModalMultiStepProps';
import { formatCurrency } from '~/utils/currencyFormatter';
import { depositAmountInCents, getDepositAmtWithFeesInCents } from '~/utils/deposits/depositAmountUtils';
import { normalizePaymentMethod } from '~/utils/normalizePaymentMethod';
import { Button } from '~/vinovest-design-system/components';
import { Alert } from '~/vinovest-design-system/components/Form/components/Alert';
import { PaymentMethodType } from '~/models/PaymentMethod';
import { usePostTransferFundsMutation } from '~/hooks/payments/usePostTransferFunds';

export const ReviewTransfer = ({ setStep }: ModalMultiStepProps) => {
    const { depositAmount, paymentMethod, destinationPortfolio, destinationPortfolioId, depositFrequency } =
        useContext(DepositContext);

    const { userId: userID, email } = useAuthentication();
    const source = normalizePaymentMethod(paymentMethod);
    const sourceName = `${source.name.toUpperCase()} ****${source.lastFour}`;
    const depositSource = `${source.type !== 'vinovest_funds' ? sourceName : source.name}`;
    const depositFrequencyLabel = getFrequencyLabel(depositFrequency);
    const depositCentsDollars = depositAmount && depositAmountInCents(depositAmount);
    const totalAmountChargedToUserCentDollars = getDepositAmtWithFeesInCents(
        depositCentsDollars,
        source.type,
        source.country as string
    );
    const { finalizeDeposit, loading, error } = useConfirmDeposit();
    const { captureEvent } = useTracking();
    const [showAcknowledgements, setShowAcknowledgements] = useState(false);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const isConfirmed = LocalStorageHelper.getItem('confirmed-acknowledgements');
    useEffect(() => {
        if (!isConfirmed && destinationPortfolio == 'managed') {
            setShowAcknowledgements(true);
        }

        if (isConfirmed) {
            setShowAcknowledgements(false);
            setIsChecked(true);
        }
    }, [isConfirmed]);
    const depositFeesInDollars = (totalAmountChargedToUserCentDollars - depositCentsDollars) / 100;
    const depositFrequencyChargeDate = dayjs().toISOString();
    const feePercentage = source.country === 'US' || source.country === '' ? '2.9%' : '4.4%';
    const { mutateAsync: postTransferFunds } = usePostTransferFundsMutation();

    const handleTransferSubmit = async (): Promise<void> => {
        try {
            if (paymentMethod.type === PaymentMethodType.VinovestFunds) {
                // Call transfer funds endpoint for portfolio transfers
                const result = await postTransferFunds({
                    amount: {
                        amount: depositAmount * 100,
                        currency: Currencies.USD
                    },
                    from: paymentMethod.id,
                    memo: 'User initiated transfer',
                    to: destinationPortfolioId
                });
                if (result && !error) {
                    setStep(5);

                    if (destinationPortfolio == 'managed') {
                        LocalStorageHelper.setItem('confirmed-acknowledgements', true);
                    }
                    return;
                }
                throw new Error('Unable to complete');
            } else {
                const completePayment = await finalizeDeposit({
                    ...source,
                    country: source.country as string,
                    startDate: depositFrequencyChargeDate
                });
                if (completePayment && !error) {
                    setStep(2);

                    if (destinationPortfolio == 'managed') {
                        LocalStorageHelper.setItem('confirmed-acknowledgements', true);
                    }

                    PosthogHelper.captureEvent(AnalyticsEventNames.DepositInitiated, {
                        paymentSource: source.type,
                        value: totalAmountChargedToUserCentDollars / 100,
                        depositAmount: totalAmountChargedToUserCentDollars / 100,
                        frequency: depositFrequencyLabel,
                        depositFeesinDollars: depositFeesInDollars,
                        destination: destinationPortfolio as string,
                        entity_type: 'lead',
                        currency: Currencies.USD,
                        userID: userID!,
                        email: email!,
                        records: [
                            {
                                deposit_initiated: true,
                                customer_id: userID!
                            }
                        ]
                    });

                    return captureEvent(AnalyticsEventNames.DepositInitiated, {
                        paymentSource: source.type,
                        value: totalAmountChargedToUserCentDollars / 100,
                        depositAmount: totalAmountChargedToUserCentDollars / 100,
                        frequency: depositFrequencyLabel,
                        depositFeesinDollars: depositFeesInDollars,
                        destination: destinationPortfolio as string,
                        entity_type: 'lead',
                        currency: Currencies.USD,
                        userID: userID!,
                        email: email!,
                        records: [
                            {
                                deposit_initiated: true,
                                customer_id: userID!
                            }
                        ]
                    });
                }
                throw new Error('Unable to complete');
            }
        } catch (completedPaymentError) {
            SentryHelper.captureException(completedPaymentError);
            return dispatchError(completedPaymentError);
        }
    };

    return (
        <div>
            <div className={'bg-gray-200 border-[1px] border-gray-200 rounded-[20px] p-4 md:p-10 mb-8'}>
                <div className={'bg-white border-[1px] border-gray-300 rounded-[20px] p-4 md:p-8'}>
                    <div className={'text-gray-600 flex mb-4'}>
                        <p className={'text-16 text-gray-600 w-1/2'}>From</p>
                        <p className={`text-[16px] w-1/2 rounded text-gray-600 text-right`}>{depositSource}</p>
                    </div>
                    <div className={'flex mb-4'}>
                        <p className={'text-16 text-gray-600 w-1/2'}>To</p>
                        <p className={'text-[16px] w-1/2 rounded text-gray-600 capitalize text-right'}>
                            {destinationPortfolio == 'managed' && `Managed Investing`}
                            {destinationPortfolio == 'trading' && `Available Balance`}
                        </p>
                    </div>
                    <div className={'flex mb-4'}>
                        <p className={'text-16 text-gray-600 w-1/2'}>Amount</p>
                        <div className={'text-[16px] w-1/2 rounded flex justify-end text-gray-600'}>
                            {formatCurrency(depositAmount, false)}
                        </div>
                    </div>
                    <div className={'flex mb-4'}>
                        <p className={'text-16 text-gray-600 w-1/2'}>Frequency</p>
                        <p className={'text-[16px] w-1/2 rounded text-gray-600 text-right'}>{depositFrequencyLabel}</p>
                    </div>
                    {source.type === 'card' && (
                        <div className={'flex mb-4'}>
                            <p className={'text-16 text-gray-600 w-1/2'}>Credit Card Fees</p>
                            <p className={'text-[16px] w-1/2 rounded text-gray-600 text-right'}>
                                <span className={'text-gray-500'}>({feePercentage})</span>{' '}
                                {formatCurrency(depositFeesInDollars, false)}
                            </p>
                        </div>
                    )}
                    <div className={'flex mb-4 border-t-[1px] border-gray-300 border-solid pt-4'}>
                        <p className={'text-16 text-gray-600 w-1/2'}>Total</p>
                        <p className={'text-[16px] w-1/2 rounded text-gray-600 text-right'}>
                            {formatCurrency(depositFeesInDollars + depositAmount, false)}
                        </p>
                    </div>
                </div>

                {Boolean(error) && (
                    <Alert classnames={'my-4 text-16 text-center'} type={'error'}>
                        Sorry, your payment could not be completed at this time.
                    </Alert>
                )}
            </div>
            {showAcknowledgements && (
                <div className={'mb-6'}>
                    <div className={'flex items-start text-[14px]'}>
                        <input
                            type={'checkbox'}
                            checked={isChecked}
                            className={'mt-[4px]'}
                            onClick={(e) => setIsChecked(e.currentTarget.checked)}
                        />{' '}
                        <p className={'text-platinum-800 ml-2'}>
                            I understand that fine wine and whiskey are long-term assets, and early exits may be limited
                            by market demand and subject to fees.
                        </p>
                    </div>
                </div>
            )}
            <div>
                <div className={'flex flex-col md:flex-row gap-[20px]'}>
                    <Button
                        variant={'secondary'}
                        onClick={() => setStep(0)}
                        classnames={'w-full md:w-1/2 text-[16px] order-last md:order-first'}
                    >
                        Back
                    </Button>
                    <Button
                        variant={'platinum'}
                        type={'submit'}
                        disabled={!isChecked}
                        classnames={`w-full md:w-1/2 text-[16px] ${loading ? 'loading-button-transition' : ''}`}
                        onClick={handleTransferSubmit}
                    >
                        {loading ? 'Processing' : 'Continue'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
